import * as THREE from 'three';
import type { PartPositionsZLevel } from '../../../services/api/loadingPlansService';

/** The engine's valid placement cells for one part orientation at one Z. */
export interface PlacementGrid {
  gridCellSize: number;
  baseOrientationTransform: number[][];
  /** Keys are `${gx},${gy}`. */
  validCells: Set<string>;
}

// The engine places a part at translation = baseOrientationTransform.translation + gridCellSize * [gx, gy]
// in X/Y, so a cell corner is where the part origin sits for that [gx, gy].

/**
 * Converts world X and Y coordinates (from part.matrix.elements[12] and elements[13])
 * to the corresponding discrete grid cell indices (gx, gy).
 */
export function worldToGridCell(
  worldX: number,
  worldY: number,
  baseTransform: number[][],
  gridCellSize: number
): { gx: number; gy: number } {
  const baseTx = baseTransform[0][3];
  const baseTy = baseTransform[1][3];
  return {
    gx: Math.round((worldX - baseTx) / gridCellSize),
    gy: Math.round((worldY - baseTy) / gridCellSize),
  };
}

/**
 * Converts discrete grid cell indices (gx, gy) back to world coordinates
 * matching the C++ NestingAlgorithmClient translation formula:
 *   worldX = baseTx + gx * gridCellSize
 *   worldY = baseTy + gy * gridCellSize
 */
export function gridCellToWorld(
  gx: number,
  gy: number,
  baseTransform: number[][],
  gridCellSize: number
): { worldX: number; worldY: number } {
  const baseTx = baseTransform[0][3];
  const baseTy = baseTransform[1][3];
  return {
    worldX: baseTx + gx * gridCellSize,
    worldY: baseTy + gy * gridCellSize,
  };
}

/**
 * Snaps a 3D matrix in X and Y to the corner of the grid cell it is over. Z is deliberately untouched:
 * the engine's grid positions say nothing about the world Z to place at.
 */
export function snapMatrixToGridCell(
  currentMatrix: THREE.Matrix4,
  baseTransform: number[][],
  gridCellSize: number
): {
  matrix: THREE.Matrix4;
  gx: number;
  gy: number;
} {
  const { gx, gy } = worldToGridCell(
    currentMatrix.elements[12],
    currentMatrix.elements[13],
    baseTransform,
    gridCellSize
  );
  const { worldX, worldY } = gridCellToWorld(gx, gy, baseTransform, gridCellSize);

  const snapped = currentMatrix.clone();
  snapped.elements[12] = worldX;
  snapped.elements[13] = worldY;

  return { matrix: snapped, gx, gy };
}

/**
 * Builds a fast O(1) lookup set of valid grid cells from the C++ engine's grid positions.
 * Keys are formatted as `${gx},${gy}`.
 */
export function buildValidCellsSet(
  gridPositions: [number, number, number][]
): Set<string> {
  const set = new Set<string>();
  if (!gridPositions) return set;
  for (let i = 0; i < gridPositions.length; i++) {
    const pos = gridPositions[i];
    set.add(`${pos[0]},${pos[1]}`);
  }
  return set;
}

/**
 * Checks whether a discrete grid cell (gx, gy) is a valid, collision-free, stable placement
 * according to the C++ engine's returned grid positions.
 */
export function isCellValid(
  gx: number,
  gy: number,
  validCellsSet: Set<string>
): boolean {
  return validCellsSet.has(`${gx},${gy}`);
}

// Code of the part-positions 422 answer: the part cannot be supported at that level in that orientation (it would
// hang below the layer it rests on), so no position there is valid.
export const PART_POSITIONS_ORIENTATION_UNFIT = 'ORIENTATION_DOES_NOT_FIT';

/** Whether a failed part-positions request is that answer rather than an error. */
export function isOrientationUnfitError(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { code?: string } } } | null)?.response;
  return response?.status === 422 && response?.data?.code === PART_POSITIONS_ORIENTATION_UNFIT;
}

export function createPlacementGrid(level: PartPositionsZLevel): PlacementGrid {
  return {
    gridCellSize: level.gridCellSize,
    baseOrientationTransform: level.baseOrientationTransform,
    validCells: buildValidCellsSet(level.gridPositions),
  };
}

/** Whether the part origin in `matrix` is over a cell the engine lists as valid. */
export function isMatrixOnValidCell(matrix: THREE.Matrix4, grid: PlacementGrid): boolean {
  const { gx, gy } = worldToGridCell(
    matrix.elements[12],
    matrix.elements[13],
    grid.baseOrientationTransform,
    grid.gridCellSize
  );
  return isCellValid(gx, gy, grid.validCells);
}
